// AUD-2 — the sound-event caption catalog + a caption resolver for any sound id
// (feeds the subtitles accessibility overlay, M23). Named non-procedural events are
// listed with a human caption; block/mob/note ids derive a caption from their
// material/species + gesture/action. PURE — no WebAudio/DOM.

export interface SoundEvent { readonly id: string; readonly subtitle: string; }

/** Named (non-procedural) sound events with their subtitle captions. */
export const SOUND_EVENTS: readonly SoundEvent[] = [
  { id: 'ui.button.click', subtitle: 'Button clicks' },
  { id: 'ui.toast.in', subtitle: 'Notification' },
  { id: 'player.levelup', subtitle: 'Player levels up' },
  { id: 'player.hurt', subtitle: 'Player hurts' },
  { id: 'item.eat', subtitle: 'Eating' },
  { id: 'entity.experience_orb.pickup', subtitle: 'Experience orb' },
  { id: 'random.pop', subtitle: 'Item picked up' },
  { id: 'ambient.cave', subtitle: 'Eerie noise' },
  { id: 'ambient.nether', subtitle: 'Nether rumbles' },
  { id: 'ambient.underwater', subtitle: 'Underwater ambience' },
  { id: 'weather.rain', subtitle: 'Rain falls' },
  { id: 'weather.thunder', subtitle: 'Thunder' },
  { id: 'block.portal.travel', subtitle: 'Portal whooshes' },
];

const NAMED = new Map(SOUND_EVENTS.map((e) => [e.id, e.subtitle]));
const titleCase = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** A human caption for any sound id (subtitles overlay). Never empty. */
export function subtitleFor(id: string): string {
  const named = NAMED.get(id);
  if (named) return named;
  const p = id.split('.');
  if (p[0] === 'block') {
    const gesture = p[p.length - 1]!;
    return gesture === 'dig' ? 'Block broken'
      : gesture === 'place' ? 'Block placed'
      : gesture === 'step' ? 'Footsteps'
      : titleCase(gesture);
  }
  if (p[0] === 'entity') {
    const species = titleCase(p[1] ?? 'mob');
    const action = p[p.length - 1];
    return action === 'death' ? `${species} dies` : action === 'hurt' ? `${species} hurts` : species;
  }
  if (p[0] === 'note_block') return 'Note Block plays';
  if (p[0] === 'music' || p[0] === 'music_disc') return 'Music';
  return titleCase(p[p.length - 1] ?? id);
}
